import { DiwaAPI } from './client'

/** Mirrors GET /graph/network in products/diwa/src/diwa/graph/router.py */
export interface GraphNode {
  id: string
  name: string
  type: string
  domain: string
  world: string | null
  self: boolean
  score: number | null
  attributes?: Record<string, unknown> | null
  /** ISO timestamp of when this entity entered the graph — powers the history scrubber. */
  created_at?: string
  /** Fresh-signal activity involving this entity — powers the heat lens. */
  heat?: number
  /** ISO timestamp of the entity's last update — powers the freshness lens. */
  updated_at?: string | null
  /** ISO timestamp of the last enrichment (profile-backed entities) — freshness lens. */
  enriched_at?: string | null
  /** Enrichment certainty 0..1 — powers the confidence lens. */
  confidence?: number | null
}

export interface GraphEdge {
  source: string
  target: string
  type: string
  /**
   * The visual grammar of trust (diwa_v2_vision §"visual grammar"): solid = confirmed,
   * dashed = inferred, dotted = pending. Backend defaults absent/junk to 'confirmed'.
   */
  provenance?: 'confirmed' | 'inferred' | 'pending'
  /** ISO timestamp of when this relation entered the graph — powers the history scrubber. */
  created_at?: string
}

export interface GraphNetwork {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

/** Mirrors GET /graph/history — weekly creation counts over the graph_events log. */
export interface GraphHistoryWeek {
  /** ISO date of the ISO-week Monday this bucket covers. */
  week_start: string
  entities_created: number
  relations_created: number
  signals_created: number
  mentions_linked: number
  enrichments: number
}

export interface GraphHistory {
  /** Ascending, zero-filled — every week in the window is present, ending on the current week. */
  weeks: GraphHistoryWeek[]
  totals: Omit<GraphHistoryWeek, 'week_start'>
}

/** Mirrors GET /graph/history?granularity=day — per-day event counts for the activity heatmap. */
export interface GraphHistoryDay {
  /** ISO date (yyyy-mm-dd, UTC) this bucket covers. */
  day: string
  /** Sum of the five growth buckets for this day. */
  total: number
  entities_created: number
  relations_created: number
  signals_created: number
  mentions_linked: number
  enrichments: number
}

export interface GraphDailyHistory {
  /** Ascending, zero-filled — every day in the window is present, ending today (UTC). */
  days: GraphHistoryDay[]
  /** Per-type sums over the window, plus `total` — the grand total. */
  totals: Omit<GraphHistoryDay, 'day'>
}

/** Mirrors GET /graph/entities/{id}/dossier. */
export interface EntityDossier {
  entity: { id: string; name: string; type: string; domain: string; attributes: Record<string, unknown> }
  notes: { id: string; title: string }[]
  interactions: { id: string; channel: string | null; type: string; summary: string; occurred_at: string | null }[]
  signals: { id: string; title: string; status: string }[]
  missions: { id: string; title: string; status: string }[]
  enrichment: { status: string; confidence: number | null; enriched_at: string | null } | null
}

export class GraphAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  network(domain?: string): Promise<GraphNetwork> {
    const qs = domain && domain !== 'all' ? `?domain=${encodeURIComponent(domain)}` : ''
    return this.api.get<GraphNetwork>(`/graph/network${qs}`)
  }

  /** Everything the app knows about one star — evidence + enrichment for the Dossier tab. */
  dossier(entityId: string): Promise<EntityDossier> {
    return this.api.get<EntityDossier>(`/graph/entities/${entityId}/dossier`)
  }

  /** GET /graph/history — per-ISO-week event counts; empty weeks arrive zero-filled. */
  history(weeks = 8): Promise<GraphHistory> {
    return this.api.get<GraphHistory>(`/graph/history?weeks=${weeks}`)
  }

  /**
   * GET /graph/history?granularity=day — per-day event counts for the activity heatmap;
   * empty days arrive zero-filled. Optionally scoped to one domain slug.
   */
  dailyHistory(days = 182, domain?: string): Promise<GraphDailyHistory> {
    const qs = domain ? `&domain=${encodeURIComponent(domain)}` : ''
    return this.api.get<GraphDailyHistory>(`/graph/history?granularity=day&days=${days}${qs}`)
  }

  /** POST /graph/entities — add a node into a domain (real; richer capture via Scry later). */
  createEntity(input: { type: string; name: string; world?: string; domain?: string }): Promise<{ id: string }> {
    return this.api.post<{ id: string }>('/graph/entities', {
      type: input.type,
      name: input.name,
      attributes: input.world ? { world: input.world } : {},
      domain: input.domain ?? 'crm',
    })
  }

  /**
   * PATCH /graph/entities/{id} — merge a partial attributes patch into one node. Passing a key as
   * null clears it. Used for the per-node colour override (attributes.color); null removes the override.
   */
  setEntityColor(entityId: string, color: string | null): Promise<{ id: string; attributes: Record<string, unknown> }> {
    return this.api.patch<{ id: string; attributes: Record<string, unknown> }>(
      `/graph/entities/${entityId}`,
      { attributes: { color } },
    )
  }

  /**
   * PATCH /graph/entities/{id} — merge an arbitrary partial attributes patch into one node.
   * Onboarding's "tell Tala about yourself" step writes `{ identity: { t0, t1, t3 } }` onto the
   * ego entity (the network node with `self: true`) through this.
   */
  patchEntityAttributes(
    entityId: string,
    attributes: Record<string, unknown>,
  ): Promise<{ id: string; attributes: Record<string, unknown> }> {
    return this.api.patch<{ id: string; attributes: Record<string, unknown> }>(
      `/graph/entities/${entityId}`,
      { attributes },
    )
  }

  /**
   * PATCH /graph/entities/{id} — rename one node through the entity update path. The backend
   * strips/bounds the name and records the same before/after event as any attribute change.
   */
  renameEntity(entityId: string, name: string): Promise<{ id: string; name: string }> {
    return this.api.patch<{ id: string; name: string }>(`/graph/entities/${entityId}`, { name })
  }

  /**
   * DELETE /graph/entities/{id} — remove one node; the backend detaches every edge touching it
   * and records an entity_deleted event so history keeps the star's before-image.
   */
  deleteEntity(entityId: string): Promise<{ deleted: boolean }> {
    return this.api.del<{ deleted: boolean }>(`/graph/entities/${entityId}`)
  }

  /** POST /graph/relations — add an edge between two existing nodes. */
  createRelation(input: { from_id: string; to_id: string; type: string; domain?: string }): Promise<{ id: string }> {
    return this.api.post<{ id: string }>('/graph/relations', {
      from_id: input.from_id,
      to_id: input.to_id,
      type: input.type,
      attributes: {},
      domain: input.domain ?? 'crm',
    })
  }
}
